import { Fragment, useState, useEffect, useMemo } from 'react';
import MenuItem from '../../../components/MenuItem';
import { bundleDisplayIdxComparer, isBundleInStock } from '../../../helpers/bundleHelpers';
import { ItemMenuHeader } from '../../../components/ItemMenuHeader';
const ItemMenu = ({
  categoryRefs,
  subCategoryHeaderRefs,
  subCategoryRefs,
  menuCategories,
  basket,
  navigateTo,
  addItem,
  removeItem,
  findItemQuantity,
  setQuickAddModalState,
  quickAddModalState,
  setMakeItAMealModalState,
  makeItAMealModalState,
  makeItAMealBundles,
  makeItAMealBundleOptions,
  makeItAMealVariant,
  makeItAMealModalTimesShown,
  searchQuery,
}) => {
  const [displayCategories, setDisplayCategories] = useState(menuCategories);

  useEffect(() => {
    setDisplayCategories(menuCategories);
    const firstItem = menuCategories
      ?.flatMap(mc => mc.subCategories ?? [])
      ?.flatMap(sc => [...(sc.bundlesToRender ?? []), ...(sc.itemsToRender ?? [])])
      ?.find(item => item.imageUrl);
    if (!firstItem) return;
    const img = new Image();
    img.src = firstItem.imageUrl;
    img.onerror = () => setDisplayCategories(
      menuCategories?.map(mc => ({
        ...mc,
        subCategories: mc.subCategories?.map(sc => ({
          ...sc,
          bundlesToRender: sc.bundlesToRender?.map(item => ({ ...item, imageUrl: item.fallbackImageUrl ?? item.imageUrl })),
          itemsToRender: sc.itemsToRender?.map(item => ({ ...item, imageUrl: item.fallbackImageUrl ?? item.imageUrl })),
        })),
      }))
    );
    return () => { img.onerror = null }
  }, [menuCategories]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery?.trim()) return displayCategories;
    const q = searchQuery.trim().toLowerCase();
    return (displayCategories ?? [])
      .map(mc => {
        const categoryMatch = mc.name.toLowerCase().includes(q);
        const filteredSubCategories = (mc.subCategories ?? [])
          .map(sc => {
            const subCatMatch = sc.name.toLowerCase().includes(q);
            if (categoryMatch || subCatMatch) return sc;
            return {
              ...sc,
              itemsToRender: (sc.itemsToRender ?? []).filter(item => {
                const nameMatch = (item.name ?? '').toLowerCase().includes(q);
                const descMatch = (item.description ?? '').toLowerCase().includes(q);
                const dietMatch = (item.dietPreferences ?? []).some(d => d.toLowerCase().includes(q));
                return nameMatch || descMatch || dietMatch;
              }),
              bundlesToRender: (sc.bundlesToRender ?? []).filter(bundle => {
                const nameMatch = (bundle.name ?? '').toLowerCase().includes(q);
                const descMatch = (bundle.description ?? '').toLowerCase().includes(q);
                return nameMatch || descMatch;
              }),
            };
          })
          .filter(sc => (sc.itemsToRender?.length > 0 || sc.bundlesToRender?.length > 0));
        return { ...mc, subCategories: filteredSubCategories };
      })
      .filter(mc => mc.subCategories?.length > 0);
  }, [displayCategories, searchQuery]);

  return (
    <>
      {searchQuery?.trim() && filteredCategories?.length === 0 && (
        <div className="menu-search-bar__no-results">
          No items found
        </div>
      )}
      {filteredCategories?.map((mc, catIdx) => (

        <Fragment key={catIdx}>

          <section
            key={`${mc.name}.${catIdx}`}
            id={`category-${mc.name.replace(/\s+/g, '-')}`}
            ref={(cat) => {
              categoryRefs.current[catIdx] = cat;
            }}
            className='is-flex-wrap-wrap'
          >
            {mc.subCategories?.length > 0 && mc.subCategories.map((sc, subcatIdx) => (
              <Fragment key={`${mc.partitionKey}.subcategory${catIdx}.${subcatIdx}`}>
                <div
                  ref={(subCat) => {
                    if (!subCategoryRefs.current[catIdx]) {
                      subCategoryRefs.current[catIdx] = [];
                    }
                    subCategoryRefs.current[catIdx][subcatIdx] = subCat;
                  }}
                  style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <ItemMenuHeader
                    category={mc}
                    subcategory={sc}
                    categoryIdx={catIdx}
                    subcatIdx={subcatIdx}
                    subCategoryHeaderRefs={subCategoryHeaderRefs}
                    navigateTo={navigateTo}
                  />
                  <div data-testid={`sub-category-${mc.subCategories?.length === 1 && subcatIdx === 0 ? mc.name : sc.name}`} className='sub-category-item-wrapper'>
                    {(sc.bundlesToRender?.length === 0 && sc.itemsToRender?.length === 0) && (
                      <>
                        <div
                          key={`${catIdx}.${subcatIdx}`}
                          className='subcategory-no-items-card'
                        >
                          No results found. Please adjust your filters.
                        </div>
                      </>)}
                    {([...(sc.bundlesToRender ?? [])])
                      .sort(bundleDisplayIdxComparer)
                      .map((el, idx) => {

                        const inStock = isBundleInStock(el, []);
                        let itemBasketIndex = -1;
                        if (basket && basket.bundles) {
                          itemBasketIndex = basket.bundles.findIndex(bItem => bItem.name === el.name);
                        }
                        return (
                          <MenuItem
                            item={el}
                            basket={basket}
                            navigateTo={navigateTo}
                            bundleItem
                            menuItem
                            columnWidth={4}
                            key={el.name}
                            outOfStock={!inStock}
                            numberInBasket={itemBasketIndex === -1 ? 0 : findItemQuantity(el.name, true)}
                            idx={idx}
                            setQuickAddModalState={setQuickAddModalState}
                            quickAddModalState={quickAddModalState}
                            setMakeItAMealModalState={setMakeItAMealModalState}
                            makeItAMealModalState={makeItAMealModalState}
                            makeItAMealBundles={makeItAMealBundles}
                            makeItAMealBundleOptions={makeItAMealBundleOptions}
                            makeItAMealVariant={makeItAMealVariant}
                            makeItAMealModalTimesShown={makeItAMealModalTimesShown}
                          />
                        );
                      })
                    }
                    {([...sc.itemsToRender] ?? [])
                      .sort((a, b) => (a.outOfStock === b.outOfStock) ? 0 : (a.outOfStock ? 1 : -1))
                      .map((item, ind) => {
                        let itemBasketIndex = -1;
                        if (basket && basket.items) {
                          itemBasketIndex = basket.items.findIndex(bItem => bItem.id === item.id && bItem.name === item.name);
                        }
                        return (
                          <Fragment key={`${mc.partitionKey}.subcategory${catIdx}.${subcatIdx}.pizza-${item.id}-${ind}`}>
                            <MenuItem
                              id={'pizza-' + ind}
                              index={ind}
                              catIndex={catIdx}
                              item={{ ...item, cost: item.displayPrice }}
                              outOfStock={item.outOfStock}
                              columnWidth={4}
                              menuItem={true}
                              bundleItem={false}
                              numberInBasket={itemBasketIndex === -1 ? 0 : findItemQuantity(item.id, false, item.name)}
                              addItem={addItem}
                              removeItem={removeItem}
                              navigateTo={navigateTo}
                              dietaryOptions={item.dietPreferences}
                              isLast={ind === sc.subProducts.length - 1}
                              setQuickAddModalState={setQuickAddModalState}
                              quickAddModalState={quickAddModalState}
                              setMakeItAMealModalState={setMakeItAMealModalState}
                              makeItAMealModalState={makeItAMealModalState}
                              makeItAMealBundles={makeItAMealBundles}
                              makeItAMealBundleOptions={makeItAMealBundleOptions}
                              makeItAMealVariant={makeItAMealVariant}
                              makeItAMealModalTimesShown={makeItAMealModalTimesShown}
                            />
                          </Fragment>
                        );
                      })
                    }
                  </div>
                </div >
              </Fragment >
            ))}
          </section>
        </Fragment>
      ))}
    </>
  );
};

export default ItemMenu;
